import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { TipoElemento, UtilityService } from '../utility/utility.service';
import { PromptAIWebService } from './prompt-ai-web.service';

export interface PromptAI {
  id: string | null;
  dataUltimaModifica: string;
  prompt: string;
  idElemento: string;
  tipo: string | undefined;
  generale: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PromptAIService {
  constructor(
    private readonly _promptAIWebService: PromptAIWebService,
    private readonly _utilityService: UtilityService,
  ) {}

  /**
   * Aggiorna un prompt esistente con un nuovo valore.
   *
   * @param {string} idPrompt - L'ID del prompt da aggiornare.
   * @param {string} prompt - Il nuovo valore del prompt da impostare.
   */
  updatePrompt(PromptAI: PromptAI) {
    return this._promptAIWebService.updatePrompt(PromptAI);
  }

  /**
   * Salva un nuovo prompt.
   *
   * @param {PromptAI} promptAI - L'oggetto che contiene i dati del prompt da salvare.
   */
  salvaPrompt(promptAI: PromptAI) {
    return this._promptAIWebService.salvaPrompt(promptAI);
  }

  /**
   * Recupera un prompt tramite ID elemento e tipo.
   *
   * @param {string} idElemento - L'ID dell'elemento per cui si desidera recuperare il prompt.
   * @param {string} tipo - Il tipo del prompt da cercare (ad esempio, 'text', 'image', ecc.).
   */
  getPromptByIdTipoElem(idElemento: string, tipoElemento: TipoElemento) {
    return this._promptAIWebService.getPromptByIdTipoElem(idElemento, tipoElemento);
  }

  /**
   * Recupera una lista di prompt con opzioni di paginazione, ricerca, filtri e ordinamento.
   *
   */
  listaPrompt(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this._promptAIWebService.listaPrompt(page, pageSize, ricerca, filters, sortBy);
  }

  /**
   * Salvataggio del prompt
   * @param promptAI
   */
  async salva(promptAI: PromptAI): Promise<PromptAI | false> {
    const isUpdate = !!promptAI.id;

    const request$ = isUpdate ? this.updatePrompt(promptAI) : this.salvaPrompt(promptAI);

    try {
      const result = await firstValueFrom(request$); // esegue la chiamata dell'observable
      this.showSuccess(isUpdate);
      return result;
    } catch (err: any) {
      this.showError(isUpdate, err.error.message);
      return false;
    }
  }

  private showSuccess(isUpdate: boolean) {
    this._utilityService.openSnackBar(
      isUpdate ? 'Aggiornamento effettuato' : 'Salvataggio effettuato',
      '',
      { duration: 2000, panelClass: ['success-snackbar'] },
    );
  }

  private showError(isUpdate: boolean, errorMessage?: string) {
    const defaultMessage = isUpdate
      ? "Errore durante l'aggiornamento."
      : 'Errore durante il salvataggio.';

    const message = errorMessage?.trim() || defaultMessage;

    this._utilityService.openSnackBar(message, '', {
      duration: 2000,
      panelClass: ['red-snackbar'],
    });
  }
}
